/**
 * HexKeyboardView Component - Unified hexagonal keyboard visualization
 * Spec §2.1: User sees ONE continuous keyboard, module boundaries are invisible
 * Spec §3: Uses global (q,r) coordinates for all keys
 */

import { useMemo, useState, useCallback, useRef } from 'react';
import type { HexCoord } from '@/types/hex';
import type { NoteMapping, LEDSettings, LEDDefaultBehavior, ScaleColoringConfig, KeyOverride, LEDOverride, Zone, ZoneBounds, LEDLayerConfig, TuningTable } from '@/types/config';
import { SCALE_MASKS } from '@/types/config';
import { generateAllCoords, layoutToPixel } from '@/lib/hexCoords';
import { calculateNote, getNoteName } from '@/lib/noteMapping';
import { useConfigStore, selectActiveTuning, selectMappingMode } from '@/store/configStore';
import { formatNoteCents, formatEdoStep, formatFrequency, formatColorsOnly, formatNoteName, formatCoordsOnly } from '@/lib/noteDisplay';
import { deriveDegreeStep, degreeToCents } from '@/lib/tuningUtils';
import type { NoteDisplayMode } from '@/lib/noteDisplay';
import { useLiveKeys } from '@/hooks/useLiveKeys';
import { HexKey } from './HexKey';
import { KeyTooltip } from './KeyTooltip';
import type { TooltipData } from './KeyTooltip';
import type { OverrideDisplayInfo, MicrotonalInfo } from './HexKey';
import type { KeyAction } from '@/types/midi';

export interface HexKeyboardViewProps {
  columns: number; // Number of module columns (1-8)
  rows: number; // Number of module rows (1-4)
  noteMapping: NoteMapping;
  ledSettings: LEDSettings;
  ledDefaultBehavior: LEDDefaultBehavior;
  scaleColoring: ScaleColoringConfig;
  zones?: Zone[]; // Zones (spec §5.6)
  overrides?: KeyOverride[]; // Key MIDI overrides (spec §5.7)
  ledOverrides?: LEDOverride[]; // Key LED overrides (spec §5.12)
  ledLayers?: LEDLayerConfig[]; // LED per layer (spec §5.13)
  selectedKey?: HexCoord | null; // Currently selected key for editing
  activeLayer?: number; // Active layer for overrides
  refreshTrigger?: number; // Optional refresh trigger to force recalculation
  hexSize?: number; // Hexagon size in pixels (default: 30)
  onKeyClick?: (coord: HexCoord) => void;
}

/**
 * HexKeyboardView - Renders the complete hexagonal keyboard
 * Shows unified grid of all keys across all modules
 */

/**
 * Convert KeyAction to display info for HexKey
 */
function actionToDisplayInfo(action: KeyAction): OverrideDisplayInfo {
  switch (action.type) {
    case 'note':
      return {
        type: 'note',
        label: 'Note',
        detail: `${action.note} ${getNoteName(action.note)}`,
      };
    case 'cc':
      return {
        type: 'cc',
        label: 'CC',
        detail: `${action.cc}=${action.value}`,
      };
    case 'program_change':
      return {
        type: 'program_change',
        label: 'PC',
        detail: `${action.program}`,
      };
    case 'chord':
      return {
        type: 'chord',
        label: 'Chord',
        detail: action.notes.map((n) => getNoteName(n)).join('+'),
      };
    case 'layer_toggle':
      return {
        type: 'layer_toggle',
        label: 'Layer',
        detail: `→${action.target_layer}`,
      };
    case 'layer_momentary':
      return {
        type: 'layer_momentary',
        label: 'Layer~',
        detail: `→${action.target_layer}`,
      };
    case 'bank_program':
      return {
        type: 'bank_program',
        label: 'Bank',
        detail: `${action.bank_msb}:${action.bank_lsb}:${action.program}`,
      };
    default:
      return { type: 'unknown', label: '?', detail: '' };
  }
}

/**
 * Check if a coordinate is within a zone's bounds
 * Spec §5.6: Zone matching
 *
 * @param coord - Hex coordinate to check
 * @param zone - Zone to check against
 * @returns True if coordinate is within zone bounds
 */
function isInZone(coord: HexCoord, zone: Zone): boolean {
  // Check Q bounds (required)
  if (coord.q < zone.bounds.q_min || coord.q > zone.bounds.q_max) {
    return false;
  }
  // Check R bounds (optional - if not specified, matches all R values)
  if (zone.bounds.r_min !== undefined && zone.bounds.r_max !== undefined) {
    if (coord.r < zone.bounds.r_min || coord.r > zone.bounds.r_max) {
      return false;
    }
  }
  return true;
}

/** Check if a coordinate is within zone bounds (for preview) */
function isInBounds(coord: HexCoord, bounds: ZoneBounds): boolean {
  if (coord.q < bounds.q_min || coord.q > bounds.q_max) return false;
  if (bounds.r_min !== undefined && bounds.r_max !== undefined) {
    if (coord.r < bounds.r_min || coord.r > bounds.r_max) return false;
  }
  return true;
}

/** Generate SVG path for flat-top hexagon (matches HexKey geometry) */
function hexOverlayPath(cx: number, cy: number, size: number): string {
  const angles = [30, 90, 150, 210, 270, 330];
  const points = angles.map((angle) => {
    const rad = (Math.PI / 180) * angle;
    return `${(cx + size * Math.cos(rad)).toFixed(2)},${(cy + size * Math.sin(rad)).toFixed(2)}`;
  });
  return `M ${points.join(' L ')} Z`;
}

/**
 * Determine scale degree color based on chromatic interval from root
 * Spec §5.14: Scale coloring by note function
 *
 * @param note - MIDI note number (0-127)
 * @param scaleColoring - Scale coloring configuration
 * @returns RGB color for the note
 */
function getScaleDegreeColor(
  note: number,
  scaleColoring: ScaleColoringConfig
): [number, number, number] {
  // Calculate chromatic interval from root (modulo 12 for octave equivalence)
  const interval = (note - scaleColoring.root_note) % 12;
  // Normalize to positive (JavaScript modulo can be negative)
  const normalizedInterval = interval < 0 ? interval + 12 : interval;

  // Per-degree coloring: return the degree's specific color
  if (scaleColoring.color_mode === 'per_degree' && scaleColoring.degree_colors?.[normalizedInterval]) {
    return scaleColoring.degree_colors[normalizedInterval];
  }

  // Three-color fallback
  if (normalizedInterval === 0) {
    return scaleColoring.colors.root;
  }

  const scaleMask = SCALE_MASKS[scaleColoring.scale_type] ?? SCALE_MASKS.major;
  if (scaleMask[normalizedInterval] === 1) {
    return scaleColoring.colors.in_scale;
  }

  return scaleColoring.colors.non_scale;
}


/**
 * Determine scale degree color for microtonal tunings.
 * Uses degree-based mapping matching firmware §5.2/§5.2.1.
 *
 * @param coord - Hex coordinate of the key
 * @param noteMapping - Note mapping with cent intervals
 * @param tuningTable - Active tuning table
 * @param scaleColoring - Scale coloring configuration (uses root_degree)
 * @returns RGB color for the key
 */
function getMicrotonalScaleColor(
  coord: HexCoord,
  noteMapping: NoteMapping,
  tuningTable: TuningTable,
  scaleColoring: ScaleColoringConfig,
  degPerQ: number,
  degPerR: number,
  degreeOffset: number = 0,
): [number, number, number] {
  const dq = coord.q - noteMapping.origin.q;
  const dr = coord.r - noteMapping.origin.r;
  const degree = dq * degPerQ + dr * degPerR + degreeOffset;
  const N = tuningTable.notes_per_period;
  const bestDegree = ((degree % N) + N) % N;

  // Per-degree coloring: return the degree's specific color
  if (scaleColoring.color_mode === 'per_degree' && scaleColoring.degree_colors?.[bestDegree]) {
    return scaleColoring.degree_colors[bestDegree];
  }

  // Three-color fallback
  const rootDegree = scaleColoring.root_degree ?? 0;
  if (bestDegree === rootDegree) {
    return scaleColoring.colors.root;
  }
  if (tuningTable.scale_mask[bestDegree]) {
    return scaleColoring.colors.in_scale;
  }
  return scaleColoring.colors.non_scale;
}

/**
 * Calculate LED color as CSS string
 * Spec §5.11: Apply local brightness, then global brightness
 * Spec §5.14: Scale coloring (priority 4)
 * Spec §5.16: LED priority hierarchy
 *
 * @param rgb - RGB color [R, G, B] (0-255)
 * @param localBrightness - Local brightness multiplier (0-1)
 * @param globalBrightness - Global brightness multiplier (0-1)
 * @param enabled - Whether LEDs are enabled
 * @returns CSS rgb() string
 */
function calculateLEDColor(
  rgb: [number, number, number],
  localBrightness: number,
  globalBrightness: number,
  enabled: boolean
): string {
  if (!enabled) {
    // LEDs disabled - show white button appearance
    return '#f0f0f0';
  }

  // Physical model: LED shines through white translucent button cap.
  // Brightness controls how much LED color shows vs the white base.
  // lerp(white, ledColor, intensity) — dim = pale/pastel, bright = vivid.
  const BASE = 240; // white button base (#f0f0f0)
  const intensity = localBrightness * globalBrightness;
  const r = Math.round(BASE + (rgb[0] - BASE) * intensity);
  const g = Math.round(BASE + (rgb[1] - BASE) * intensity);
  const b = Math.round(BASE + (rgb[2] - BASE) * intensity);

  return `rgb(${r}, ${g}, ${b})`;
}

export function HexKeyboardView({
  columns,
  rows,
  noteMapping,
  ledSettings,
  ledDefaultBehavior,
  scaleColoring,
  zones = [],
  overrides = [],
  ledOverrides = [],
  ledLayers = [],
  selectedKey = null,
  activeLayer = 0,
  refreshTrigger = 0,
  hexSize = 30,
  onKeyClick,
}: HexKeyboardViewProps) {
  const keysPerModule = useConfigStore((state) => state.keysPerModule);
  const stagger = useConfigStore((state) => state.stagger);
  const moduleRowShift = useConfigStore((state) => state.moduleRowShift);
  const activeTuning = useConfigStore(selectActiveTuning);
  const mappingMode = useConfigStore(selectMappingMode);
  const tuningBaseFrequency = useConfigStore((state) => state.tuningBaseFrequency);
  const noteDisplayMode = useConfigStore((state) => state.noteDisplayMode);
  const setNoteDisplayMode = useConfigStore((state) => state.setNoteDisplayMode);
  const liveVisualizationEnabled = useConfigStore((state) => state.liveVisualizationEnabled);
  const zonePreview = useConfigStore((state) => state.zonePreview);

  // Tooltip state
  const [hoveredKey, setHoveredKey] = useState<HexCoord | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Live key visualization
  const pressedKeys = useLiveKeys(liveVisualizationEnabled);

  const handleHoverEnter = useCallback((coord: HexCoord) => {
    setHoveredKey(coord);
  }, []);

  const handleHoverLeave = useCallback(() => {
    setHoveredKey(null);
  }, []);

  // Generate all coordinates for the grid (spec §3.3)
  const allKeys = useMemo(() => {
    return generateAllCoords(columns, rows, keysPerModule, stagger, moduleRowShift);
  }, [columns, rows, keysPerModule, stagger, moduleRowShift]);

  // Calculate pixel positions, notes, and LED colors for all keys
  // Spec §3.2: Use layout (x,y) for visualization, hex (q,r) for MIDI
  // Spec §5.11: Calculate LED colors with brightness
  // Spec §5.14: Scale coloring (priority 4)
  // Spec §5.16: LED priority hierarchy
  const keyData = useMemo(() => {
    // Calculate max Y for multi-module grids (spec §3.3)
    // Formula: maxY = (rows - 1) × MODULE_SPACING_R + 4
    const maxY = (rows - 1) * 4 + 4;

    // Pre-compute degree steps for tuning-based mapping (once, not per key)
    const degPerQ = activeTuning
      ? deriveDegreeStep(noteMapping.interval_q_cents ?? 0, activeTuning) : 0;
    const degPerR = activeTuning
      ? deriveDegreeStep(noteMapping.interval_r_cents ?? 0, activeTuning) : 0;

    return allKeys.map((key) => {
      const pixel = layoutToPixel(key.layout, hexSize, maxY);

      // Find zone for this key (spec §5.6) - needed before note computation
      const zoneIndex = zones.findIndex((z) => isInZone(key.hex, z));
      const zone = zoneIndex >= 0 ? zones[zoneIndex] : undefined;

      // Use zone-specific note mapping if available, otherwise global
      const effectiveMapping = zone?.note_mapping ?? noteMapping;
      const rawNote = calculateNote(key.hex, effectiveMapping);
      // Apply zone transpose on top (matches firmware behavior)
      const note = zone ? Math.max(0, Math.min(127, rawNote + zone.transpose)) : rawNote;
      const noteName = getNoteName(note);

      // Pre-compute zone-aware tuning vars (used by both scale coloring and labels)
      const effMapping = (activeTuning && zone?.note_mapping) ? effectiveMapping : noteMapping;
      const effDegPerQ = (activeTuning && zone?.note_mapping)
        ? deriveDegreeStep(effectiveMapping.interval_q_cents ?? 0, activeTuning) : degPerQ;
      const effDegPerR = (activeTuning && zone?.note_mapping)
        ? deriveDegreeStep(effectiveMapping.interval_r_cents ?? 0, activeTuning) : degPerR;
      const zoneDegOffset = (activeTuning && zone)
        ? deriveDegreeStep((zone.transpose_cents ?? zone.transpose * 1000), activeTuning) : 0;

      // Check for overrides on this key (spec §5.7, §5.12)
      // Layer actions (toggle/momentary) are cross-layer: visible on all layers
      const keyOverride = overrides.find(
        (o) => o.pos.q === key.hex.q && o.pos.r === key.hex.r &&
          (o.layer === activeLayer ||
           o.action.type === 'layer_toggle' || o.action.type === 'layer_momentary')
      );
      const hasOverride = !!keyOverride;
      const overrideInfo = keyOverride ? actionToDisplayInfo(keyOverride.action) : undefined;

      const ledOverride = ledOverrides.find(
        (o) => o.pos.q === key.hex.q && o.pos.r === key.hex.r && o.layer === activeLayer
      );
      const hasLEDOverride = !!ledOverride;

      // Check for LED layer config for active layer (spec §5.13)
      const ledLayerConfig = ledLayers.find((l) => l.layer === activeLayer);

      // Determine LED color based on priority hierarchy (spec §5.16)
      // Priority 0: Layer toggle/momentary keys show target layer's color
      // Priority 1: LED overrides (if present)
      // Priority 2: LED layer config (if present and mode is not 'off')
      // Priority 4: Scale coloring (if enabled)
      // Priority 5: Default idle state
      let baseColor: [number, number, number];
      let localBrightness: number;

      // Layer toggle keys: show target layer's color (bright if active, dim if not)
      const layerActionTarget =
        keyOverride?.action.type === 'layer_toggle' ? keyOverride.action.target_layer :
        keyOverride?.action.type === 'layer_momentary' ? keyOverride.action.target_layer :
        undefined;
      const targetLayerConfig = layerActionTarget !== undefined
        ? ledLayers.find((l) => l.layer === layerActionTarget)
        : undefined;

      if (layerActionTarget !== undefined && targetLayerConfig) {
        // Layer toggle key: use target layer's base_color
        baseColor = targetLayerConfig.base_color;
        localBrightness = (layerActionTarget === activeLayer)
          ? targetLayerConfig.brightness
          : targetLayerConfig.brightness * 0.25;
      } else if (ledOverride) {
        // Priority 1: Use LED override color
        baseColor = ledOverride.idle.color;
        localBrightness = ledOverride.idle.brightness;
      } else if (ledSettings.enabled && scaleColoring.enabled) {
        // Priority 2 (when scale active): Scale coloring takes precedence over layer coloring
        if (activeTuning) {
          baseColor = getMicrotonalScaleColor(key.hex, effMapping, activeTuning, scaleColoring, effDegPerQ, effDegPerR, zoneDegOffset);
        } else {
          baseColor = getScaleDegreeColor(note, scaleColoring);
        }
        localBrightness = ledDefaultBehavior.idle.brightness;
      } else if (ledLayerConfig && ledLayerConfig.mode !== 'off') {
        // Priority 3: Use LED layer config (skipped when scale coloring is active)
        baseColor = ledLayerConfig.base_color;
        if (ledLayerConfig.mode === 'dim') {
          localBrightness = ledLayerConfig.brightness * 0.5;
        } else {
          localBrightness = ledLayerConfig.brightness;
        }
      } else {
        // Priority 4: Use default idle color
        baseColor = ledDefaultBehavior.idle.color;
        localBrightness = ledDefaultBehavior.idle.brightness;
      }

      let ledColor = calculateLEDColor(
        baseColor,
        localBrightness,
        ledSettings.brightness,
        ledSettings.enabled
      );

      // Zone-based dimming: darken odd-indexed zones post-render.
      // Applied after the white-blend model to ensure visible contrast.
      if (zoneIndex >= 0 && zoneIndex % 2 === 1 && ledSettings.enabled) {
        const DIM = 0.85;
        const match = ledColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (match) {
          ledColor = `rgb(${Math.round(Number(match[1]) * DIM)}, ${Math.round(Number(match[2]) * DIM)}, ${Math.round(Number(match[3]) * DIM)})`;
        }
      }

      // Compute display labels based on noteDisplayMode
      let microtonalInfo: MicrotonalInfo | undefined;
      if (activeTuning) {
        if (noteDisplayMode === 'colors_only') {
          microtonalInfo = formatColorsOnly();
        } else if (noteDisplayMode === 'coords_only') {
          microtonalInfo = formatCoordsOnly();
        } else if (noteDisplayMode === 'note_name') {
          microtonalInfo = formatNoteName(noteName);
        } else if (noteDisplayMode === 'note_number') {
          // Default rendering (note number + name) — leave undefined
        } else {
          // Microtonal modes: zone-aware degree-based mapping
          const dq = key.hex.q - effMapping.origin.q;
          const dr = key.hex.r - effMapping.origin.r;
          const keyDegree = dq * effDegPerQ + dr * effDegPerR + zoneDegOffset;
          const quantizedOffset = degreeToCents(keyDegree, activeTuning);
          const N = activeTuning.notes_per_period;
          const degree = ((keyDegree % N) + N) % N;

          const totalCents = effMapping.base_note * 100 + quantizedOffset / 10;

          if (noteDisplayMode === 'note_cents') {
            const nearest = Math.round(totalCents / 100);
            const deviation = totalCents - nearest * 100;
            microtonalInfo = formatNoteCents(
              Math.max(0, Math.min(127, nearest)),
              deviation
            );
          } else if (noteDisplayMode === 'frequency') {
            const pitchLabel = activeTuning.pitch_labels?.[degree];
            microtonalInfo = formatFrequency(quantizedOffset, tuningBaseFrequency, pitchLabel);
          } else {
            // 'edo_step'
            microtonalInfo = formatEdoStep(degree, N, quantizedOffset / 10);
          }
        }
      } else {
        // Non-tuning general modes
        if (noteDisplayMode === 'colors_only') {
          microtonalInfo = formatColorsOnly();
        } else if (noteDisplayMode === 'coords_only') {
          microtonalInfo = formatCoordsOnly();
        } else if (noteDisplayMode === 'note_name') {
          microtonalInfo = formatNoteName(noteName);
        }
        // 'note_number' — leave undefined (default HexKey rendering)
      }

      return {
        coord: key.hex, // (q,r) - NORMATIVE for MIDI/LED
        pixel,
        note,
        noteName,
        ledColor, // CSS color string
        hasOverride,
        hasLEDOverride,
        overrideInfo,
        microtonalInfo,
        zone, // Zone info for display
        zoneIndex, // Index into zones array (-1 if none)
      };
    });
  }, [allKeys, hexSize, noteMapping, rows, ledSettings, ledDefaultBehavior, scaleColoring, zones, overrides, ledOverrides, ledLayers, activeLayer, refreshTrigger, activeTuning, tuningBaseFrequency, noteDisplayMode]);

  // Calculate SVG viewBox to fit all hexagons
  const viewBox = useMemo(() => {
    if (keyData.length === 0) {
      return { minX: 0, minY: 0, width: 100, height: 100 };
    }

    // Find bounds with padding for hex size
    const padding = hexSize * 1.5;
    const xs = keyData.map((k) => k.pixel.x);
    const ys = keyData.map((k) => k.pixel.y);

    const minX = Math.min(...xs) - padding;
    const maxX = Math.max(...xs) + padding;
    const minY = Math.min(...ys) - padding;
    const maxY = Math.max(...ys) + padding;

    return {
      minX,
      minY,
      width: maxX - minX,
      height: maxY - minY,
    };
  }, [keyData, hexSize]);

  const totalKeys = columns * rows * keysPerModule;

  // Compute tooltip data for hovered key
  const tooltipData: TooltipData | null = useMemo(() => {
    if (!hoveredKey) return null;
    const kd = keyData.find((k) => k.coord.q === hoveredKey.q && k.coord.r === hoveredKey.r);
    if (!kd) return null;

    // Transform SVG coords to container-relative pixel coords
    const svg = svgRef.current;
    const container = containerRef.current;
    if (!svg || !container) return null;

    const svgRect = svg.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    // SVG point -> screen point via viewBox transform
    const scaleX = svgRect.width / viewBox.width;
    const scaleY = svgRect.height / viewBox.height;
    const pixelX = (kd.pixel.x - viewBox.minX) * scaleX + svgRect.left - containerRect.left;
    const pixelY = (kd.pixel.y - viewBox.minY) * scaleY + svgRect.top - containerRect.top;

    const channel = kd.zone ? kd.zone.channel : 0;

    // Compute frequency for tooltip (zone-aware)
    let frequency: string | undefined;
    if (activeTuning) {
      const zone = kd.zone;
      const ttEffMapping = zone?.note_mapping ?? noteMapping;
      const ttDegPerQ = zone?.note_mapping
        ? deriveDegreeStep(ttEffMapping.interval_q_cents ?? 0, activeTuning)
        : deriveDegreeStep(noteMapping.interval_q_cents ?? 0, activeTuning);
      const ttDegPerR = zone?.note_mapping
        ? deriveDegreeStep(ttEffMapping.interval_r_cents ?? 0, activeTuning)
        : deriveDegreeStep(noteMapping.interval_r_cents ?? 0, activeTuning);
      const ttZoneDegOffset = zone
        ? deriveDegreeStep((zone.transpose_cents ?? zone.transpose * 1000), activeTuning) : 0;
      const dq = hoveredKey.q - ttEffMapping.origin.q;
      const dr = hoveredKey.r - ttEffMapping.origin.r;
      const keyDegree = dq * ttDegPerQ + dr * ttDegPerR + ttZoneDegOffset;
      const quantizedOffset = degreeToCents(keyDegree, activeTuning);
      const totalCentsFromA4 = (ttEffMapping.base_note - 69) * 100 + quantizedOffset / 10;
      const freq = tuningBaseFrequency * Math.pow(2, totalCentsFromA4 / 1200);
      frequency = `${freq.toFixed(1)} Hz`;
    } else {
      const freq = 440 * Math.pow(2, (kd.note - 69) / 12);
      frequency = `${freq.toFixed(1)} Hz`;
    }

    return {
      coord: kd.coord,
      note: kd.note,
      noteName: kd.noteName,
      channel,
      zoneName: kd.zone?.name,
      overrideType: kd.overrideInfo?.type,
      overrideDetail: kd.overrideInfo?.detail,
      frequency,
      pixelX,
      pixelY,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredKey, keyData, viewBox, activeTuning, noteMapping, tuningBaseFrequency]);

  return (
    <div ref={containerRef} style={{ width: '100%', maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
      <div
        style={{
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '13px',
          color: '#666',
        }}
      >
        <span>
          {totalKeys} keys ({columns}×{rows} modules)
        </span>
        <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontSize: '12px' }}>Labels:</span>
          <select
            value={noteDisplayMode}
            onChange={(e) => setNoteDisplayMode(e.target.value as NoteDisplayMode)}
            style={{
              padding: '2px 6px',
              fontSize: '12px',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              backgroundColor: '#fff',
            }}
          >
            <optgroup label="General">
              <option value="note_number">Note Number</option>
              <option value="note_name">Note Name</option>
              <option value="coords_only">Coordinates Only</option>
              <option value="colors_only">Colors Only</option>
            </optgroup>
            {mappingMode === 'microtonal' && (
              <optgroup label="Microtonal">
                <option value="edo_step">Degree\N</option>
                <option value="note_cents">Note + Cents</option>
                <option value="frequency">Frequency</option>
              </optgroup>
            )}
          </select>
        </label>
      </div>

      <svg
        ref={svgRef}
        width="100%"
        height="auto"
        viewBox={`${viewBox.minX} ${viewBox.minY} ${viewBox.width} ${viewBox.height}`}
        style={{
          border: '1px solid #e9ecef',
          borderRadius: '4px',
          backgroundColor: '#fafafa',
        }}
      >
        {/* Render all hexagonal keys with LED visualization */}
        {keyData.map(({ coord, pixel, note, noteName, ledColor, hasOverride, hasLEDOverride, overrideInfo, microtonalInfo }) => {
          const liveState = pressedKeys.get(`${coord.q},${coord.r}`);
          return (
            <HexKey
              key={`${coord.q},${coord.r}`}
              coord={coord}
              note={note}
              noteName={noteName}
              x={pixel.x}
              y={pixel.y}
              size={hexSize}
              ledColor={ledColor}
              isSelected={selectedKey?.q === coord.q && selectedKey?.r === coord.r}
              hasOverride={hasOverride}
              hasLEDOverride={hasLEDOverride}
              overrideInfo={overrideInfo}
              microtonalInfo={microtonalInfo}
              isPressed={!!liveState}
              pressVelocity={liveState?.velocity ?? 0}
              onHoverEnter={handleHoverEnter}
              onHoverLeave={handleHoverLeave}
              {...(onKeyClick ? { onClick: onKeyClick } : {})}
            />
          );
        })}

        {/* Zone preview overlay (on top, shown while editing) */}
        {zonePreview && keyData.map(({ coord, pixel }) => {
          if (!isInBounds(coord, zonePreview)) return null;
          return (
            <path
              key={`zone-preview-${coord.q},${coord.r}`}
              d={hexOverlayPath(pixel.x, pixel.y, hexSize)}
              fill="#0088ff"
              fillOpacity={0.1}
              stroke="#0088ff"
              strokeWidth={2.5}
              strokeDasharray="4 3"
              style={{ pointerEvents: 'none' }}
            />
          );
        })}
      </svg>

      {/* Hover tooltip */}
      <KeyTooltip
        info={tooltipData}
        containerWidth={containerRef.current?.clientWidth ?? 800}
        containerHeight={containerRef.current?.clientHeight ?? 600}
      />

      {/* Key label legend */}
      <div
        style={{
          marginTop: '4px',
          fontSize: '11px',
          color: '#999',
          textAlign: 'center',
        }}
      >
        <b>q,r</b> = hex coordinates
      </div>
    </div>
  );
}
